import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';

import { ErrorMessage, Loading, withValidPermission } from '@Components';
import { useFetchData } from '@CustomHooks';
import { Permission } from '@Models';
import { bopsApi } from '@Network';
import { buildScheduleEnrichment } from './enrichment';
import Schedules from './schedules';

const SchedulesContainer = () => {
  const { t } = useTranslation();

  const { data, isLoading, error, refetch } = useFetchData(bopsApi.getSchedules);
  const {
    data: assignments,
    isLoading: isLoadingAssignments,
    error: assignmentsError,
  } = useFetchData(bopsApi.getScheduleStoreAssignments);
  const {
    data: contracts,
    isLoading: isLoadingContracts,
    error: contractsError,
  } = useFetchData(bopsApi.getFieldServiceContracts);
  const {
    data: fieldServices,
    isLoading: isLoadingFieldServices,
    error: fieldServicesError,
  } = useFetchData(bopsApi.getFieldServices);

  const enrichmentById = useMemo(
    () => buildScheduleEnrichment(assignments ?? [], contracts ?? [], fieldServices ?? []),
    [assignments, contracts, fieldServices],
  );

  useEffect(() => {
    if (error && data) showErrorToast(t('Schedule.RefreshError'));
  }, [data, error, t]);

  if ((isLoading && !data) || isLoadingAssignments || isLoadingContracts || isLoadingFieldServices) {
    return <Loading title={t('Schedule.Loading')} />;
  }

  if (!data || assignmentsError || contractsError || fieldServicesError) {
    return (
      <ErrorMessage
        internalMessage={`Unable to get schedules: ${String(
          error ?? assignmentsError ?? contractsError ?? fieldServicesError,
        )}`}
      />
    );
  }

  return (
    <Schedules
      schedules={data}
      enrichmentById={enrichmentById}
      fieldServices={fieldServices ?? []}
      onSchedulesChanged={() => refetch()}
    />
  );
};

export default withValidPermission(SchedulesContainer, [Permission.ViewSchedules, Permission.ManageSchedules]);
